import React from 'react';
import { BusinessIntelligence } from '../../../types';

interface BusinessIntelligenceSectionProps {
  data: BusinessIntelligence;
}

export const BusinessIntelligenceSection: React.FC<BusinessIntelligenceSectionProps> = ({ data }) => {
  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical': return { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' };
      case 'high': return { bg: '#fed7aa', text: '#9a3412', border: '#fdba74' };
      case 'medium': return { bg: '#fef3c7', text: '#92400e', border: '#fde047' };
      case 'low': return { bg: '#dcfce7', text: '#14532d', border: '#86efac' };
      default: return { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' };
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'critical': return '#ef4444';
      case 'high': return '#f97316';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getComplexityIcon = (complexity: string) => {
    switch (complexity.toLowerCase()) {
      case 'low': return '🟢';
      case 'medium': return '🟡';
      case 'high': return '🔴';
      default: return '⚪';
    }
  };

  return (
    <div style={{ marginBottom: '3rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem' }}>
          Business Intelligence Analysis
        </h2>
        <p style={{ color: '#6b7280' }}>
          Key insights extracted from your assessment responses
        </p>
      </div>

      {/* Current State */}
      {data.tech_stack.length > 0 && (
        <div style={{ 
          background: '#eff6ff', 
          border: '1px solid #bfdbfe', 
          borderRadius: '12px', 
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e3a8a', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '0.5rem' }}>🏢</span>
            Current Technology Stack
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {data.tech_stack.map((tech, idx) => (
              <span
                key={idx}
                style={{ 
                  padding: '0.5rem 1rem', 
                  background: '#dbeafe', 
                  color: '#1e40af', 
                  borderRadius: '9999px', 
                  fontSize: '0.875rem', 
                  fontWeight: '500' 
                }}
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Current Processes */}
      {data.current_processes.length > 0 && (
        <div style={{ 
          background: '#ffffff', 
          border: '1px solid #e5e7eb', 
          borderRadius: '12px', 
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '0.5rem' }}>⚙️</span>
            Current Processes
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {data.current_processes.map((process, idx) => (
              <div key={idx} style={{ borderLeft: '4px solid #3b82f6', paddingLeft: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <h4 style={{ fontWeight: '600', color: '#111827' }}>{process.name}</h4>
                  <span style={{ 
                    padding: '0.25rem 0.5rem', 
                    background: '#f3f4f6', 
                    color: '#374151', 
                    borderRadius: '4px', 
                    fontSize: '0.75rem' 
                  }}>
                    Maturity: {process.maturity.replace('_', ' ')}
                  </span>
                </div>
                <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.5rem' }}>{process.description}</p>
                {process.is_manual && (
                  <span style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    padding: '0.25rem 0.5rem', 
                    background: '#fef3c7', 
                    color: '#92400e', 
                    fontSize: '0.75rem', 
                    borderRadius: '4px' 
                  }}>
                    ⚠️ Manual Process
                  </span>
                )}
                {process.bottlenecks.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Bottlenecks:</p>
                    <ul style={{ listStyle: 'disc', listStylePosition: 'inside', fontSize: '0.75rem', color: '#6b7280' }}>
                      {process.bottlenecks.map((bottleneck, bidx) => (
                        <li key={bidx}>{bottleneck}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pain Points */}
      {data.pain_points.length > 0 && (
        <div style={{ 
          background: '#ffffff', 
          border: '1px solid #e5e7eb', 
          borderRadius: '12px', 
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '0.5rem' }}>⚠️</span>
            Identified Pain Points
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {data.pain_points.map((pain, idx) => {
              const colors = getSeverityColor(pain.severity);
              return (
                <div
                  key={idx}
                  style={{ 
                    borderLeft: `4px solid ${colors.border}`, 
                    paddingLeft: '1rem', 
                    padding: '0.75rem', 
                    borderRadius: '0 8px 8px 0',
                    background: colors.bg
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <span style={{ fontWeight: '600', fontSize: '0.875rem', textTransform: 'uppercase', color: colors.text }}>
                          {pain.severity}
                        </span>
                        {pain.area && (
                          <span style={{ 
                            fontSize: '0.75rem', 
                            padding: '0.125rem 0.5rem', 
                            background: '#ffffff', 
                            borderRadius: '4px' 
                          }}>
                            {pain.area}
                          </span>
                        )}
                      </div>
                      <p style={{ color: '#111827', fontWeight: '500' }}>{pain.issue}</p>
                      {pain.time_impact && (
                        <p style={{ fontSize: '0.875rem', color: '#374151', marginTop: '0.25rem' }}>
                          <span style={{ fontWeight: '500' }}>Impact:</span> {pain.time_impact}
                        </p>
                      )}
                      {pain.automation_potential && (
                        <p style={{ fontSize: '0.875rem', color: '#374151', marginTop: '0.25rem' }}>
                          <span style={{ fontWeight: '500' }}>Automation Potential:</span> {pain.automation_potential}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AI Opportunities */}
      {data.ai_opportunities.length > 0 && (
        <div style={{ 
          background: 'linear-gradient(to bottom right, #faf5ff, #eff6ff)', 
          border: '1px solid #e9d5ff', 
          borderRadius: '12px', 
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '0.5rem' }}>🤖</span>
            AI & Automation Opportunities
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
            {data.ai_opportunities.map((opp, idx) => (
              <div
                key={idx}
                style={{ 
                  background: '#ffffff', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '12px', 
                  padding: '1rem',
                  transition: 'box-shadow 0.2s',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ 
                      width: '8px', 
                      height: '8px', 
                      borderRadius: '9999px', 
                      background: getPriorityColor(opp.priority) 
                    }} />
                    <span style={{ fontWeight: 'bold', color: '#111827', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                      {opp.priority}
                    </span>
                  </div>
                  <span style={{ 
                    fontSize: '0.75rem', 
                    padding: '0.25rem 0.5rem', 
                    background: '#f3e8ff', 
                    color: '#6b21a8', 
                    borderRadius: '4px', 
                    fontWeight: '500' 
                  }}>
                    {opp.type}
                  </span>
                </div>
                
                <h4 style={{ fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>{opp.area}</h4>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.75rem' }}>{opp.description}</p>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem' }}>
                    <span style={{ color: '#6b7280', width: '80px' }}>Impact:</span>
                    <span style={{ color: '#111827', fontWeight: '500' }}>{opp.impact}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem' }}>
                    <span style={{ color: '#6b7280', width: '80px' }}>Complexity:</span>
                    <span style={{ color: '#111827' }}>
                      {getComplexityIcon(opp.complexity)} {opp.complexity}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Business Context */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
        {/* Volume Metrics */}
        <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '1rem' }}>
          <h4 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem', fontSize: '0.875rem', display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '0.5rem' }}>📊</span>
            Business Size
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {data.volume_metrics.employees && (
              <div>
                <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Employees</p>
                <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827' }}>
                  {data.volume_metrics.employees}
                </p>
              </div>
            )}
            {data.volume_metrics.customers && (
              <div>
                <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Customers</p>
                <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827' }}>
                  {data.volume_metrics.customers.toLocaleString()}
                </p>
              </div>
            )}
            {data.volume_metrics.transactions_monthly && (
              <div>
                <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Monthly Transactions</p>
                <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827' }}>
                  {data.volume_metrics.transactions_monthly.toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Urgency */}
        <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '1rem' }}>
          <h4 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem', fontSize: '0.875rem', display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '0.5rem' }}>⏰</span>
            Urgency Level
          </h4>
          <div>
            <p style={{ 
              fontSize: '1.5rem', 
              fontWeight: 'bold',
              color: data.urgency.urgency_level === 'high' ? '#dc2626' :
                     data.urgency.urgency_level === 'medium' ? '#ca8a04' :
                     '#16a34a'
            }}>
              {data.urgency.urgency_level.toUpperCase()}
            </p>
            {data.urgency.timeline && (
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                Timeline: {data.urgency.timeline}
              </p>
            )}
          </div>
        </div>

        {/* Data Readiness */}
        <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '1rem' }}>
          <h4 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem', fontSize: '0.875rem', display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '0.5rem' }}>💾</span>
            Data Readiness
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem' }}>
              <span style={{ color: data.data_readiness.has_structured_data ? '#16a34a' : '#dc2626' }}>
                {data.data_readiness.has_structured_data ? '✅' : '❌'}
              </span>
              <span style={{ marginLeft: '0.5rem', color: '#374151' }}>Structured Data</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem' }}>
              <span style={{ color: !data.data_readiness.data_silos_exist ? '#16a34a' : '#dc2626' }}>
                {!data.data_readiness.data_silos_exist ? '✅' : '⚠️'}
              </span>
              <span style={{ marginLeft: '0.5rem', color: '#374151' }}>
                {data.data_readiness.data_silos_exist ? 'Data Silos Exist' : 'No Data Silos'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

